import { useState } from "react";
import "./App.css";

// Import components
import {
  AssetDetailsModal,
  EquipmentCard,
  EquipmentDetailsModal,
  Header,
  Sidebar,
} from "./components";

// Import custom hooks
import { useEquipmentData, useWebSocket, useParameterBounds, useDarkMode } from "./hooks";

// Import utilities
import { getApiBaseUrl, getDisplayBaseUrl } from "./utils/api";

function App() {
  const apiBaseUrl = getApiBaseUrl();
  const displayBaseUrl = getDisplayBaseUrl();

  // Theme
  const { darkMode, toggleDarkMode } = useDarkMode();

  // Custom hooks for data management
  const { liveData, timeSeriesData, wsConnected } = useWebSocket(apiBaseUrl);
  const { equipmentData, assetsData, loading, error, refetch } =
    useEquipmentData(apiBaseUrl);
  const { parameterBounds } = useParameterBounds(timeSeriesData);

  // Modal state
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAssetForModal, setSelectedAssetForModal] = useState(null);
  const [assetModalOpen, setAssetModalOpen] = useState(false);

  // Filter states
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedIndustries, setSelectedIndustries] = useState([]);
  const [parameterRange, setParameterRange] = useState({ min: 0, max: 10 });
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedAssets, setSelectedAssets] = useState([]);

  // Get version from environment variables (set during build)
  const version = process.env.REACT_APP_VERSION || "development";

  // Derived filter options from equipment data
  const availableTypes = [
    ...new Set(equipmentData.map((eq) => eq.type)),
  ].sort();
  const availableIndustries = [
    ...new Set(equipmentData.map((eq) => eq.industry)),
  ].sort();
  const availableAssets = assetsData.map((asset) => ({
    id: asset.id,
    name: asset.name,
  }));

  // Filter equipment
  const filteredEquipment = equipmentData.filter((equipment) => {
    // Filter by selected assets (multiple)
    if (selectedAssets.length > 0 && !selectedAssets.includes(equipment.assetId)) {
      return false;
    }

    // Filter by type
    if (selectedTypes.length > 0 && !selectedTypes.includes(equipment.type)) {
      return false;
    }

    // Filter by industry
    if (
      selectedIndustries.length > 0 &&
      !selectedIndustries.includes(equipment.industry)
    ) {
      return false;
    }

    // Filter by parameter count
    const paramCount = equipment.parameters.length;
    if (paramCount < parameterRange.min || paramCount > parameterRange.max) {
      return false;
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesName = equipment.name.toLowerCase().includes(query);
      const matchesId = equipment.id.toLowerCase().includes(query);
      const matchesType = equipment.type.toLowerCase().includes(query);
      if (!matchesName && !matchesId && !matchesType) {
        return false;
      }
    }

    return true;
  });

  // Filter handlers
  const toggleTypeFilter = (type) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const toggleIndustryFilter = (industry) => {
    setSelectedIndustries((prev) =>
      prev.includes(industry)
        ? prev.filter((i) => i !== industry)
        : [...prev, industry]
    );
  };

  const toggleAssetFilter = (assetId) => {
    setSelectedAssets((prev) =>
      prev.includes(assetId)
        ? prev.filter((id) => id !== assetId)
        : [...prev, assetId]
    );
  };

  const clearFilters = () => {
    setSelectedTypes([]);
    setSelectedIndustries([]);
    setSearchQuery("");
    setParameterRange({ min: 0, max: 10 });
    setSelectedAssets([]);
  };

  // Equipment modal handlers
  const handleMoreDetails = (equipment) => {
    setSelectedEquipment(equipment);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedEquipment(null);
  };

  // Asset handlers
  const handleAssetDetails = (asset) => {
    setSelectedAssetForModal(asset);
    setAssetModalOpen(true);
  };

  const handleCloseAssetModal = () => {
    setAssetModalOpen(false);
    setSelectedAssetForModal(null);
  };

  // Show loading state
  if (loading) {
    return (
      <div className="App">
        <Header wsConnected={false} darkMode={darkMode} onToggleDarkMode={toggleDarkMode} />
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading equipment data...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="App">
        <Header wsConnected={false} darkMode={darkMode} onToggleDarkMode={toggleDarkMode} />
        <div className="error-container">
          <p>Error loading equipment data: {error}</p>
          <button onClick={refetch}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      {/* Header */}
      <Header wsConnected={wsConnected} darkMode={darkMode} onToggleDarkMode={toggleDarkMode} />

      {/* Main Content */}
      <div className="main-container">
        {/* Sidebar */}
        <Sidebar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          selectedIndustries={selectedIndustries}
          availableIndustries={availableIndustries}
          onToggleIndustry={toggleIndustryFilter}
          selectedTypes={selectedTypes}
          availableTypes={availableTypes}
          onToggleType={toggleTypeFilter}
          parameterRange={parameterRange}
          onParameterRangeChange={setParameterRange}
          selectedAssets={selectedAssets}
          availableAssets={availableAssets}
          onToggleAsset={toggleAssetFilter}
          onAssetDetails={handleAssetDetails}
          assetsData={assetsData}
          onClearFilters={clearFilters}
          filteredCount={filteredEquipment.length}
        />

        {/* Equipment Grid */}
        <main className="content">
          <div className="content-header">
            <h1 className="page-title">Equipment Catalog</h1>
            <p className="page-subtitle">
              Browse available equipment and their real-time parameters
            </p>
          </div>

          {/* Equipment Section Header */}
          <div className="equipment-section-header">
            <h2 className="section-title-main">
              {selectedAssets.length > 0
                ? `Equipment in ${selectedAssets.length} Asset${selectedAssets.length > 1 ? "s" : ""}`
                : "All Equipment"}
            </h2>
            <span className="equipment-count-badge">
              {filteredEquipment.length} items
            </span>
          </div>

          <div className="equipment-grid">
            {filteredEquipment.map((equipment) => (
              <EquipmentCard
                key={equipment.id}
                equipment={equipment}
                onMoreDetails={() => handleMoreDetails(equipment)}
              />
            ))}
          </div>

          {filteredEquipment.length === 0 && (
            <div className="no-results">
              <p>No equipment found matching your filters.</p>
              <button className="reset-btn" onClick={clearFilters}>
                Reset Filters
              </button>
            </div>
          )}
        </main>
      </div>

      {/* Equipment Details Modal */}
      {modalOpen && selectedEquipment && (
        <EquipmentDetailsModal
          equipment={selectedEquipment}
          liveData={liveData}
          timeSeriesData={timeSeriesData}
          parameterBounds={parameterBounds}
          apiBaseUrl={apiBaseUrl}
          displayBaseUrl={displayBaseUrl}
          onClose={handleCloseModal}
          darkMode={darkMode}
        />
      )}

      {/* Asset Details Modal */}
      {assetModalOpen && selectedAssetForModal && (
        <AssetDetailsModal
          asset={selectedAssetForModal}
          displayBaseUrl={displayBaseUrl}
          onClose={handleCloseAssetModal}
        />
      )}

      {/* Version Footer */}
      <footer className="app-footer">
        <span>{version}</span>
      </footer>
    </div>
  );
}

export default App;
